import { OrgRoles, Roles } from "@aura-frontend/omnibus-az-client";
import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAppContext } from "@/hooks/use-app-context";
import { hasPermissionForPath } from "@/lib/permissions";
import { ROUTES } from "@/lib/routes";

import { ErrorScreen } from "./ErrorScreen";
import { LoadingScreen } from "./LoadingScreen";

interface AuthenticatedScreenProps {
  children: ReactNode;
}

const AuthenticatedScreen = ({ children }: AuthenticatedScreenProps) => {
  const location = useLocation();
  const {
    user,
    currentOrganization,
    isAuthenticated,
    isLoading,
    error,
    userOrganizations,
  } = useAppContext();

  // Show loading screen while determining auth state
  if (isLoading) {
    return <LoadingScreen />;
  }
  if (error) {
    return <ErrorScreen />;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return (
      <Navigate
        to={ROUTES.LOGIN}
        state={{
          from: `${location.pathname}${location.search}${location.hash}`,
        }}
        replace
      />
    );
  }

  // Wait for both organizations and currentOrganization to be fully loaded
  // userOrganizations can be loaded but currentOrganization undefined if orgId hasn't synced yet
  if (
    user &&
    userOrganizations &&
    userOrganizations.length > 0 &&
    !currentOrganization
  ) {
    return <LoadingScreen />;
  }

  // Combine user roles and org roles
  const userRoles = (user?.roles || []) as Roles[];
  const orgRoles = (currentOrganization?.orgRoles || []) as OrgRoles[];
  const allRoles = [...userRoles, ...orgRoles];

  // Check if user has permission for this path
  const hasPermission = hasPermissionForPath(allRoles, location.pathname);

  if (!hasPermission) {
    const redirectPath =
      allRoles.length === 0 ? ROUTES.NO_ACCESS : ROUTES.NOT_FOUND;
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

export default AuthenticatedScreen;
